import { useAuthStore } from "@/features/auth/useAuthStore"
import { api, buildErrorMessage } from "@/shared/api-client"
import type { AgentBrief, Message, RunnerEvent, Session } from "./types"
import { runSseLoop } from "./_sseLoop"
import { pingKind } from "./_runFollow"
import { requestApproval } from "@/shared/approval" // jarvis-r-a

export interface ProjectBrief {
  id: string
  name: string
  agent_id: string
  status: string
}

export const chatApi = {
  listSessions: () => api.get<Session[]>("/sessions"),
  createSession: (agent_id: string, title?: string, project_id?: string) =>
    api.post<Session>("/sessions", { agent_id, title, project_id }),
  deleteSession: (id: string) => api.delete<void>(`/sessions/${id}`),
  handover: (id: string) => api.post<{ written: boolean; reason?: string }>(`/sessions/${id}/handover`, {}),
  updateSession: (id: string, fields: { title?: string; status?: string; model_override?: string | null; reasoning_effort?: string | null; project_id?: string | null; buddy_mode?: string }) =>
    api.patch<Session>(`/sessions/${id}`, fields),
  // limit lädt nur die neuesten N — lange Sessions würden sonst bei jedem
  // Reload den kompletten Verlauf übertragen (gemessen: 9.667 Nachrichten, 41 MB).
  listMessages: (id: string, limit?: number) =>
    api.get<Message[]>(`/sessions/${id}/messages${limit ? `?limit=${limit}` : ""}`),
  logCmd: (id: string, user_text: string, assistant_text: string) =>
    api.post<{ ok: boolean }>(`/sessions/${id}/log-cmd`, { user_text, assistant_text }),
  listAgents: () => api.get<AgentBrief[]>("/agents"),
  listProjects: () => api.get<ProjectBrief[]>("/projects"),
  compact: (id: string) => api.post<{
    skipped?: boolean
    reason_code?: string
    reason_params?: Record<string, unknown>
    summarized_count?: number
    kept_count?: number
    tokens_before?: number
  }>(`/sessions/${id}/compact`, {}),
  tokens: (id: string) => api.get<{
    used: number
    context_window: number
    compact_threshold: number
    model: string | null
    message_count?: number
    max_turns?: number
  }>(`/sessions/${id}/tokens`),
  toolConfirm: (sessionId: string, callId: string, decision: "approve" | "deny") =>
    api.post<{ resolved: boolean }>(`/sessions/${sessionId}/tool-confirm/${callId}`, { decision }),
  /** Stoppt den laufenden Agent-Run serverseitig — funktioniert immer, auch nach
   *  Reconnect (cancelt den Server-Task, nicht die Verbindung). */
  stopRun: (sessionId: string) =>
    api.post<{ stopped: boolean }>(`/sessions/${sessionId}/stop`, {}),
  /** Läuft für die Session gerade ein Run? + aktuelle Event-Seq (für Reconnect). */
  runStatus: (sessionId: string) =>
    api.get<{ running: boolean; latest_seq: number }>(`/sessions/${sessionId}/run-status`),
}

/** Stream a user message through SSE and yield runner events as they arrive. */
export async function* sendMessage(
  sessionId: string,
  text: string,
  files: File[] = [],
  signal?: AbortSignal,
  resendMessageId?: string,
): AsyncIterable<RunnerEvent> {
  const token = useAuthStore.getState().token
  const body = new FormData()
  body.append("text", text)
  for (const file of files) body.append("files", file, file.name)
  // local-* IDs existieren nur im Frontend-State, nie in der DB → als neue Nachricht senden
  const effectiveResendId = resendMessageId?.startsWith("local-") ? undefined : resendMessageId
  const url = effectiveResendId
    ? `/api/sessions/${sessionId}/messages/${encodeURIComponent(effectiveResendId)}/resend`
    : `/api/sessions/${sessionId}/messages`
  const res = await fetch(url, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body,
    signal,
  })

  // jarvis-r-a: Befehle zum administrativen Modus beantwortet der Server als JSON (nicht an das Modell)
  if (res.ok && (res.headers.get("content-type") ?? "").includes("application/json")) {
    const data = (await res.json().catch(() => ({}))) as { admin_mode?: { action?: string }; jarvis_control?: { reply?: string; decide?: { id: string; decision: string } } }
    const act = data.admin_mode?.action
    const say = (text: string): RunnerEvent => ({ type: "text", text })
    const jc = data.jarvis_control // jarvis-register
    if (jc?.reply) {
      yield say(jc.reply)
      if (jc.decide) {
        try {
          await api.post(`/control/requests/${jc.decide.id}/decision`, { decision: jc.decide.decision })
          yield say("Готово: запрос выполняется. Сообщу о результате.")
        } catch (e) {
          yield say("Не выполнено: " + (e instanceof Error ? e.message : ""))
        }
      }
    } else if (act === "prompt_code") {
      yield say("Нужно подтверждение владельца: введите код Google Authenticator в открывшемся окне.")
      const ok = await requestApproval({ kind: "enter_admin_mode" })
      yield say(ok ? "Режим администрирования включён: 15 минут бездействия, не более 60 минут." : "Режим администрирования не включён.")
    } else if (act === "exited") {
      yield say("Режим администрирования выключен.")
    } else if (act === "denied") {
      yield say("Переход в режим администрирования недоступен для этой учётной записи.")
    }
    yield { type: "done", message_id: "", iterations: 0, input_tokens: 0, output_tokens: 0, cache_creation_tokens: 0, cache_read_tokens: 0 }
    return
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const msg = res.status === 409
      ? "Agent läuft noch – bitte kurz warten"
      : buildErrorMessage(body, res.status)
    throw new Error(msg)
  }
  if (!res.body) throw new Error("Kein Response-Body")

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const frames = buffer.split("\n\n")
    buffer = frames.pop() ?? ""

    for (const frame of frames) {
      const event = parseSseFrame(frame)
      if (event) yield event
    }
  }
}

/** Hängt sich an einen bereits laufenden Run und streamt dessen Events ab
 *  `afterSeq` lückenlos weiter (Reconnect mitten im Lauf ohne Token-Verlust). */
export async function* attachRun(
  sessionId: string,
  afterSeq: number,
  signal?: AbortSignal,
): AsyncIterable<RunnerEvent> {
  const token = useAuthStore.getState().token
  const res = await fetch(
    `/api/sessions/${sessionId}/attach?after_seq=${afterSeq}`,
    { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }, signal },
  )
  if (!res.ok || !res.body) return
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const frames = buffer.split("\n\n")
    buffer = frames.pop() ?? ""
    for (const frame of frames) {
      const event = parseSseFrame(frame)
      if (event) yield event
    }
  }
}

function parseSseFrame(frame: string): RunnerEvent | null {
  let data = ""
  for (const line of frame.split("\n")) {
    if (line.startsWith("data: ")) data += line.slice(6)
  }
  if (!data) return null
  try {
    return JSON.parse(data) as RunnerEvent
  } catch {
    return null
  }
}

/**
 * Live-Sync v1: abonniert den Session-Broadcast-Kanal. Ruft `onPing` bei jedem
 * Aktivitäts-Ping (ein Lauf macht Fortschritt). Läuft bis `signal` abbricht und
 * reconnectet bei Verbindungsabriss. EventSource scheidet aus (kann keinen
 * Authorization-Header), darum fetch + getReader wie bei sendMessage.
 */
export async function subscribeSession(
  sessionId: string,
  onPing: (kind: string) => void,
  signal: AbortSignal,
): Promise<void> {
  await runSseLoop({
    url: `/api/sessions/${sessionId}/stream`,
    token: () => useAuthStore.getState().token,
    // Ein Frame mit data:-Zeile = Ping (Keepalive sind reine :-Kommentare).
    onFrame: (frame) => { const kind = pingKind(frame); if (kind !== null) onPing(kind) },
    onUnauthorized: () => useAuthStore.getState().logout(),
    signal,
  })
}

export interface ActivityEntry {
  session_id: string
  agent_id: string
  name: string
  project_id: string | null
  current_tool: string | null
}

/**
 * Abonniert den globalen Agent-Aktivitäts-Feed (SSE). Ruft `onSnapshot` mit der
 * jeweils vollen Momentaufnahme laufender Agenten. Reconnect bei Abriss.
 */
export async function subscribeAgentActivity(
  onSnapshot: (agents: ActivityEntry[]) => void,
  signal: AbortSignal,
): Promise<void> {
  await runSseLoop({
    url: "/api/agents/activity/stream",
    token: () => useAuthStore.getState().token,
    onFrame: (frame) => {
      const dataLine = frame.split("\n").find((l) => l.startsWith("data:"))
      if (!dataLine) return
      try {
        onSnapshot(JSON.parse(dataLine.slice(5).trim()) as ActivityEntry[])
      } catch { /* unvollständiger Frame ignorieren */ }
    },
    onUnauthorized: () => useAuthStore.getState().logout(),
    signal,
  })
}
